import React, { useEffect, useState } from 'react';
import type { TProviderRecentlyWorked } from '../../home/types';
import { Avatar, Link, Row, Stack, Text, TextArea, Title } from 'library/ui';
import { Colors } from 'library';
import { Rating } from '@mui/material';
import { Check } from '@mui/icons-material';
import { IProviderReviewOption, ProviderReviewSubmitResponse } from 'models';
import { Controller, useForm } from 'react-hook-form';
import ReviewOptionSelect from './ReviewOptionsSelect';
import { useClient } from 'shared';

interface ReviewFormValues {
    text?: string;
    options?: number[];
    score?: number;
    needs_improvement?: number[];
    going_well?: number[];
}

export default function ProviderReviewModalLine({
    worker,
    options,
    businessLocationId,
}: {
    worker: TProviderRecentlyWorked;
    options: IProviderReviewOption[];
    businessLocationId?: number;
}) {
    const [isOpen, setOpen] = useState(false);
    const { send, loading, data, error } = useClient<ProviderReviewSubmitResponse>({
        url: 'api/provider-review/',
        options: { sendImmediately: false },
    });
    const {
        control,
        formState: { isDirty },
        handleSubmit,
        watch,
        reset,
    } = useForm<ReviewFormValues>();

    const totalScore = watch('score');
    const selectedOptions = watch(['options', 'going_well', 'needs_improvement']);

    const positiveOptions = options.filter((x) => x.is_positive);
    const negativeOptions = options.filter((x) => x.is_negative);
    const otherOptions = options.filter((x) => !x.is_positive && !x.is_negative);

    const onSubmit = (values: ReviewFormValues) => {
        const optionsSelected = [
            ...(values.options ?? []),
            ...(values.going_well ?? []),
            ...(values.needs_improvement ?? []),
        ];

        send({
            body: {
                reviewee: worker.id,
                total_score: values.score,
                text: values.text,
                review_results: options
                    .filter((option) => optionsSelected?.includes(option.id))
                    .map((it) => ({
                        option: it.id,
                    })),
                business_location: businessLocationId,
            },
        }).then(() => {
            setOpen(false);
        });
    };

    useEffect(() => {
        reset({
            text: data?.text,
            score: data?.total_score,
            options: options
                .filter(
                    (it) => !it.is_negative && !it.is_positive && data?.review_results?.some((i) => i.option === it.id),
                )
                .map((it) => it.id),
            going_well: options
                .filter((it) => it.is_positive && data?.review_results?.some((i) => i.option === it.id))
                .map((it) => it.id),
            needs_improvement: options
                .filter((it) => it.is_negative && data?.review_results?.some((i) => i.option === it.id))
                .map((it) => it.id),
        });
    }, [data, reset]);

    const buttonDetails = (() => {
        if (!isOpen) {
            return {
                text: data ? 'Edit' : 'Review',
                action: () => setOpen(true),
            };
        }
        if (isDirty) {
            return {
                text: 'Submit',
                action: totalScore && selectedOptions?.length ? handleSubmit(onSubmit) : () => {},
                disabled: !totalScore || !selectedOptions?.length,
            };
        }

        return {
            text: 'Close',
            action: () => setOpen(false),
        };
    })();

    const initials = `${worker.user.first_name?.[0] ?? ''}${worker.user.last_name?.[0] ?? ''}`;

    return (
        <Stack gap="md">
            <Row gap="md" align="center" justify="between" wrap>
                <Row gap="md" align="center">
                    <Avatar imageUrl={worker.profile_image?.image} initials={initials} size={64} />
                    <Title size="h3">
                        {worker.user.first_name} {worker.user.last_name}
                    </Title>
                </Row>
                <Row gap="md" align="center">
                    {!!data && (
                        <Row gap="sm" align="center">
                            <Text weight="semibold">Reviewed</Text>
                            <Check sx={{ fontSize: 18, color: Colors.secondary }} />
                        </Row>
                    )}
                    {loading ? (
                        <Text size="sm" tone="secondary">
                            Saving…
                        </Text>
                    ) : (
                        <Link
                            onClick={buttonDetails.action}
                            color={buttonDetails.disabled ? Colors.textSecondary : undefined}
                        >
                            {buttonDetails.text}
                        </Link>
                    )}
                </Row>
            </Row>
            {!!error && (
                <Text tone="error">
                    There was an error submitting your review. Please refresh your page and try again.
                </Text>
            )}
            {isOpen && (
                <Stack gap="md">
                    <Stack gap="sm">
                        <Text weight="bold">Provide a star rating</Text>
                        <Controller
                            control={control}
                            name="score"
                            render={({ field }) => (
                                <Rating
                                    size="large"
                                    value={field.value ?? 0}
                                    name="Overall"
                                    onChange={(_, value) => field.onChange(value)}
                                />
                            )}
                        />
                    </Stack>
                    {totalScore && (
                        <>
                            <Text size="sm" weight="bold">
                                Select at least one to continue
                            </Text>
                            <Stack gap="sm">
                                <Text size="sm" weight="bold">
                                    What is this worker doing well?
                                </Text>
                                <Controller
                                    control={control}
                                    name="going_well"
                                    render={({ field }) => (
                                        <ReviewOptionSelect
                                            value={field.value ?? []}
                                            onChange={field.onChange}
                                            options={positiveOptions}
                                            isScorePositive={true}
                                        />
                                    )}
                                />
                            </Stack>
                            <Stack gap="sm">
                                <Text size="sm" weight="bold">
                                    What can this worker improve on?
                                </Text>
                                <Controller
                                    control={control}
                                    name="needs_improvement"
                                    render={({ field }) => (
                                        <ReviewOptionSelect
                                            value={field.value ?? []}
                                            onChange={field.onChange}
                                            options={negativeOptions}
                                            isScorePositive={false}
                                        />
                                    )}
                                />
                            </Stack>
                            <Stack gap="sm">
                                <Text weight="bold">
                                    Which of the following can this worker do confidently and independently? (Select all
                                    that apply)
                                </Text>
                                <Controller
                                    control={control}
                                    name="options"
                                    render={({ field }) => (
                                        <ReviewOptionSelect
                                            value={field.value ?? []}
                                            onChange={field.onChange}
                                            options={otherOptions}
                                            isScorePositive={false}
                                            isScoreNeutral={true}
                                        />
                                    )}
                                />
                            </Stack>
                        </>
                    )}
                    <Stack gap="sm">
                        <Text weight="bold">Additional feedback</Text>
                        <Controller
                            control={control}
                            name="text"
                            render={({ field }) => (
                                <TextArea value={field.value ?? ''} onChange={field.onChange} fullWidth minRows={4} />
                            )}
                        />
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}
